import unescape from 'lodash.unescape'
import { FILE_TYPE_NAME_MESSAGES, FILE_TYPE_PARENT_SEPARATOR } from '@/constants/FILE_SUBFOLDERS'

// File types ("file groups") can be organized into folders by naming them with
// a ">" separator, e.g. "Design > Drawings for Installation" → folder "Design".
// No data migration is required: admins simply add ">" to the relevant names.

// Sentinel for file types that have no ">" in their name.
export const FILE_FOLDER_OTHER = '__other__'

const FOLDER_SEPARATOR = FILE_TYPE_PARENT_SEPARATOR

// The backend currently returns file-group names HTML-escaped (">" comes back as
// "&gt;"). Always unescape before parsing or displaying a group name.
// TODO: Verify API usage — backend should stop double-escaping the name server-side.
export function unescapeGroupName(name?: string | null): string {
  return unescape(name ?? '')
}

// Returns the folder label (trimmed text before the first ">"), or null when the
// name has no ">" (i.e. it belongs to the "Other" bucket).
export function getFolderName(name?: string | null): string | null {
  const unescaped = unescapeGroupName(name)
  const separatorIndex = unescaped.indexOf(FOLDER_SEPARATOR)
  if (separatorIndex === -1) {
    return null
  }
  const folder = unescaped.slice(0, separatorIndex).trim()
  return folder || null
}

export type FileGroupLike = { id?: number, label?: string | null, name?: string | null }

// Distinct, alphabetically-sorted folder names across all provided file groups.
// Accepts both option-shaped objects ({ label }) and raw groups ({ name }).
export function getFolderNames(groups: FileGroupLike[] = []): string[] {
  const folders = new Set<string>()
  groups.forEach((group) => {
    const folder = getFolderName(group.label ?? group.name)
    if (folder) {
      folders.add(folder)
    }
  })
  return [...folders].sort((a, b) => a.localeCompare(b))
}

const getGroupName = (group: FileGroupLike): string => unescapeGroupName(group.label ?? group.name).trim()

// The name as the tree shows it: "Sales>Quotes" and "Sales > Quotes" land in
// the same place, so they count as the same name.
const getComparableName = (name: string): string => {
  const { parent, fileType } = splitFileTypeName(name)
  return parent === null ? fileType : `${parent}${FOLDER_SEPARATOR}${fileType}`
}

// Parent and file type parts of a name. Without a usable parent ("Invoice",
// "> Drawings") the whole trimmed name is the file type, matching getFolderName.
export function splitFileTypeName(name?: string | null): { parent: string | null; fileType: string } {
  const unescaped = unescapeGroupName(name).trim()
  const parent = getFolderName(unescaped)
  if (parent === null) {
    return { parent: null, fileType: unescaped }
  }
  const separatorIndex = unescaped.indexOf(FOLDER_SEPARATOR)
  return { parent, fileType: unescaped.slice(separatorIndex + 1).trim() }
}

export function countParentSeparators(name?: string | null): number {
  return unescapeGroupName(name).split(FOLDER_SEPARATOR).length - 1
}

// "Parents in use" on the admin page (AC4-2): every parent with the number of
// file types that use it.
export function getParentsInUse(groups: FileGroupLike[] = []): { parent: string; count: number }[] {
  const counts = new Map<string, number>()
  groups.forEach((group) => {
    const parent = getFolderName(group.label ?? group.name)
    if (parent) {
      counts.set(parent, (counts.get(parent) ?? 0) + 1)
    }
  })
  return [...counts.entries()]
    .map(([parent, count]) => ({ parent, count }))
    .sort((a, b) => a.parent.localeCompare(b.parent))
}

// The file type a name collides with, matched the way the uniqueness rule below
// matches ("Sales > Quotes" finds "Sales&gt;Quotes"), or undefined.
export function findFileTypeByName<T extends FileGroupLike>(name: string, groups: T[] = []): T | undefined {
  const comparableValue = getComparableName(unescapeGroupName(name).trim())
  return groups.find((group) => getComparableName(getGroupName(group)) === comparableValue)
}

// The frontend copy of the backend's file type name rules, run live on create
// and rename. Returns the first message that applies, or null when valid.
export function validateFileTypeName(
  name: string,
  groups: FileGroupLike[] = [],
  ignoreGroupId?: number,
): string | null {
  const value = unescapeGroupName(name).trim()
  if (!value) {
    return FILE_TYPE_NAME_MESSAGES.required
  }

  const separatorCount = countParentSeparators(value)
  if (separatorCount > 1) {
    return FILE_TYPE_NAME_MESSAGES.oneSeparatorOnly
  }

  if (separatorCount === 1 && value.split(FOLDER_SEPARATOR).some((part) => !part.trim())) {
    return FILE_TYPE_NAME_MESSAGES.emptyPart
  }

  const otherGroups = groups.filter((group) => ignoreGroupId === undefined || group.id !== ignoreGroupId)

  const comparableValue = getComparableName(value)
  if (otherGroups.some((group) => getComparableName(getGroupName(group)) === comparableValue)) {
    return FILE_TYPE_NAME_MESSAGES.unique
  }

  if (separatorCount === 0) {
    const isParent = otherGroups.some((group) => getFolderName(group.label ?? group.name) === value)
    return isParent ? FILE_TYPE_NAME_MESSAGES.alreadyParent : null
  }

  const parent = getFolderName(value)
  const isStandaloneType = parent !== null && otherGroups.some((group) => (
    countParentSeparators(group.label ?? group.name) === 0 && getGroupName(group) === parent
  ))
  return isStandaloneType && parent ? FILE_TYPE_NAME_MESSAGES.parentIsFileType(parent) : null
}
